import { normalizarTextoLinea, opcionesTextoLibre } from '@siiv/schemas';

// Pure rules behind the directory's line filters (R65/R66). The client component only renders; which lines a
// Facultad/Unidad selection offers, how custom texts are de-duplicated, and what the search and "Sólo con
// líneas" read are decided here so they can be tested without React. Relative imports only (test runner).

/** Value of the "no filter" choice, shared with the client's select filters. */
export const TODAS = 'all';

/** The slice of an investigator these rules read (structural: the real `InvestigatorRecord` satisfies it). */
export interface AlcanceInvestigador {
	/** Faculty / centre of the PRINCIPAL unit. */
	macroUnit?: string | null;
	/** Name of the PRINCIPAL unit. */
	unit?: string | null;
	researchLineSlugs: readonly string[];
	customLines?: readonly string[];
	/** Estamento shown for the researcher (the "Estamento" filter compares against it). */
	rolInvestigadorNombre?: string | null;
}

/** An institutional line as the directory payload describes it. */
export interface LineaDelDirectorio {
	slug: string;
	name: string;
	/** Owner unit, '' for a transversal line (or when the owner is a unit the researcher hides). */
	unit: string;
	unitShort?: string | null;
}

/**
 * Registers a line seen on a researcher's directory entry. A line is shared by many researchers, but its owner
 * unit reaches each entry only when that researcher does not hide it (`unit` is '' otherwise). The first entry
 * seen must therefore NOT decide the unit: a later entry that carries it fills the gap, so the result depends on
 * the data and not on the order of the directory. A unit nobody exposes stays '' (nothing leaks).
 */
export function registrarLineaDelDirectorio<T extends LineaDelDirectorio>(mapa: Map<string, T>, linea: T): void {
	const existente = mapa.get(linea.slug);
	if (!existente) {
		mapa.set(linea.slug, linea);
		return;
	}
	if (existente.unit.trim() === '' && linea.unit.trim() !== '') {
		existente.unit = linea.unit;
		existente.unitShort = linea.unitShort ?? null;
	} else if (!existente.unitShort && linea.unit === existente.unit && linea.unitShort) {
		existente.unitShort = linea.unitShort;
	}
}

export interface OpcionLinea {
	value: string;
	label: string;
	/** Short owner unit shown after the name; '' for a transversal line. */
	unit: string;
	/** Full text for the tooltip: the name, plus the full owner unit name when there is one. */
	title: string;
}

/**
 * The researchers a Facultad + Unidad selection leaves in the directory, matched on the principal unit
 * (`macroUnit`, `unit`). Search and Estamento do not narrow this: the option lists cascade only from
 * Facultad and Unidad, as the Unidad list already cascades only from Facultad.
 */
export function investigadoresEnAlcance<T extends AlcanceInvestigador>(
	investigadores: readonly T[],
	facultad: string,
	unidad: string,
): T[] {
	return investigadores.filter(
		(i) => (facultad === TODAS || i.macroUnit === facultad) && (unidad === TODAS || i.unit === unidad),
	);
}

/** Distinct units (principal unit) of the researchers of a faculty, or of everybody when none is chosen. */
export function unidadesDeFacultad(investigadores: readonly AlcanceInvestigador[], facultad: string): string[] {
	return Array.from(
		new Set(
			investigadores
				.filter((i) => facultad === TODAS || i.macroUnit === facultad)
				.map((i) => i.unit)
				.filter((unidad): unidad is string => Boolean(unidad)),
		),
	).sort((a, b) => a.localeCompare(b, 'es'));
}

/**
 * Options of the institutional "Línea UDA" filter: the distinct lines held by the researchers in scope, sorted by
 * name and then by unit so equal names from different units are told apart (RLM-07). Slugs the catalog does
 * not know are skipped.
 */
export function opcionesDeLinea(
	alcance: readonly AlcanceInvestigador[],
	lineas: ReadonlyMap<string, LineaDelDirectorio>,
): OpcionLinea[] {
	const slugs = new Set<string>();
	for (const investigador of alcance) {
		for (const slug of investigador.researchLineSlugs) slugs.add(slug);
	}

	const opciones: OpcionLinea[] = [];
	for (const slug of slugs) {
		const linea = lineas.get(slug);
		if (!linea) continue;
		const unidadCompleta = linea.unit.trim();
		opciones.push({
			value: linea.slug,
			label: linea.name,
			unit: (linea.unitShort?.trim() || unidadCompleta),
			title: unidadCompleta ? `${linea.name} · ${unidadCompleta}` : linea.name,
		});
	}

	return opciones.sort(
		(a, b) => a.label.localeCompare(b.label, 'es') || a.unit.localeCompare(b.unit, 'es') || a.value.localeCompare(b.value),
	);
}

/** The directory's select filters except the custom-lines one (which is applied afterwards, see below). */
export interface FiltrosDelListado {
	facultad: string;
	unidad: string;
	linea: string;
	estamento: string;
}

/**
 * Facultad, Unidad (both on the PRINCIPAL unit), Línea UDA and Estamento. The custom-lines filter is
 * deliberately not a field here: the researchers that pass this predicate (plus the search text and the content
 * checkboxes, which the client adds) are the pool the custom-lines options are listed from, and the custom
 * filter then narrows that pool into the results. Keeping it out is what makes its own list follow the
 * visible results without ever narrowing itself.
 */
export function coincideConFiltros(investigador: AlcanceInvestigador, filtros: FiltrosDelListado): boolean {
	return (
		(filtros.facultad === TODAS || investigador.macroUnit === filtros.facultad) &&
		(filtros.unidad === TODAS || investigador.unit === filtros.unidad) &&
		(filtros.linea === TODAS || investigador.researchLineSlugs.includes(filtros.linea)) &&
		(filtros.estamento === TODAS || investigador.rolInvestigadorNombre === filtros.estamento)
	);
}

/**
 * Options of the custom-lines filter ("Línea"): the distinct custom texts of the researchers it is given, equal
 * ignoring case and accents. `value` is the normalized key, `label` the most frequent spelling.
 *
 * A custom line belongs to no unit, but the list must follow what is on screen: `visibles` are the researchers
 * that pass the search text and EVERY other filter (Facultad, Unidad, Línea UDA, Estamento, content checkboxes),
 * and NOT the custom-lines filter itself, so the chosen text stays listed next to its alternatives.
 *
 * `seleccion` (a normalized key) is never dropped: when the other filters leave nobody holding it, it is still
 * listed — spelled as it appears in `todos`, the whole directory — so the select keeps showing the current value
 * instead of an empty-looking list. Nothing resets it because of another filter.
 */
export function opcionesDeLineaPersonalizada(
	visibles: readonly AlcanceInvestigador[],
	seleccion: string = TODAS,
	todos: readonly AlcanceInvestigador[] = visibles,
): { value: string; label: string }[] {
	const textos = visibles.flatMap((i) => i.customLines ?? []);
	if (seleccion !== TODAS && !textos.some((texto) => normalizarTextoLinea(texto) === seleccion)) {
		textos.push(
			...todos.flatMap((i) => i.customLines ?? []).filter((texto) => normalizarTextoLinea(texto) === seleccion),
		);
	}
	return opcionesTextoLibre(textos).map((opcion) => ({ value: opcion.clave, label: opcion.etiqueta }));
}

/** True when the researcher holds a custom line equal to `clave` under the normalization. 'all' passes. */
export function tieneLineaPersonalizada(investigador: AlcanceInvestigador, clave: string): boolean {
	if (clave === TODAS) return true;
	return (investigador.customLines ?? []).some((texto) => normalizarTextoLinea(texto) === clave);
}

/** "Sólo con líneas": at least one institutional line OR one non-blank custom line (CRL-09). */
export function tieneAlgunaLinea(investigador: AlcanceInvestigador): boolean {
	return (
		investigador.researchLineSlugs.length > 0 ||
		(investigador.customLines ?? []).some((texto) => normalizarTextoLinea(texto) !== '')
	);
}

/** Line texts the directory search reads: institutional names, then custom texts (CRL-07). */
export function textosDeLineasParaBuscar(
	investigador: AlcanceInvestigador,
	nombrePorSlug: ReadonlyMap<string, string>,
): string[] {
	return [
		...investigador.researchLineSlugs.map((slug) => nombrePorSlug.get(slug) ?? ''),
		...(investigador.customLines ?? []),
	];
}

/** A selection that is no longer offered goes back to "all" (the Unidad filter's behaviour); otherwise it stays. */
export function seleccionVigente(valor: string, opciones: readonly { value: string }[]): string {
	if (valor === TODAS) return TODAS;
	return opciones.some((opcion) => opcion.value === valor) ? valor : TODAS;
}

/** "N en <unidad>" when a unit is chosen, else "N en <facultad>"; nothing when neither is set. */
export function leyendaDeAlcance(facultad: string, unidad: string, cuenta: number): string | undefined {
	if (unidad !== TODAS) return `${cuenta} en ${unidad}`;
	if (facultad !== TODAS) return `${cuenta} en ${facultad}`;
	return undefined;
}

/**
 * The cascading selections of the directory: Facultad -> Unidad -> Línea UDA. The custom-lines filter ("Línea")
 * is NOT part of the cascade: a custom line belongs to no unit, so no Facultad/Unidad change can drop it.
 */
export interface FiltrosDeAlcance {
	facultad: string;
	unidad: string;
	linea: string;
}

/**
 * Cascade rule Facultad -> Unidad -> Línea UDA, evaluated in ONE step so no selection is judged against a stale
 * one: the Unidad resets when the faculty no longer contains it, and the institutional line is then checked
 * against the options of the NEXT faculty + unit. A selection is reset only when it is absent from those options
 * (RLM-06 S2); one that is still offered survives a faculty change.
 */
export function resolverCascada(
	investigadores: readonly AlcanceInvestigador[],
	lineas: ReadonlyMap<string, LineaDelDirectorio>,
	filtros: FiltrosDeAlcance,
): FiltrosDeAlcance {
	const unidad =
		filtros.unidad === TODAS || unidadesDeFacultad(investigadores, filtros.facultad).includes(filtros.unidad)
			? filtros.unidad
			: TODAS;
	const alcance = investigadoresEnAlcance(investigadores, filtros.facultad, unidad);
	return {
		facultad: filtros.facultad,
		unidad,
		linea: seleccionVigente(filtros.linea, opcionesDeLinea(alcance, lineas)),
	};
}
